/**
 * Left navigation rail.
 *
 * Replaces the horizontal top bar the app used to carry. A vertical rail is
 * what the Intelliport design calls for, and it earns the change: the nav list
 * is free to grow without competing for horizontal room with the device
 * selector, user controls and version strip, all of which previously shared one
 * 60px line and were already crowding each other out.
 *
 * Below MOBILE_BP the rail goes off-canvas behind a hamburger, because 216px of
 * a phone's width is most of the screen.
 */
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import { apiScopes } from "../auth/msalConfig";
import { isLocalAuth } from "../auth/localAuth";

import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { useAppContext } from "../context/AppContext";
import { theme } from "../theme";
import intelliportLogo from "../assets/intelliport-logo.svg";
import intelliportLogoDark from "../assets/intelliport-logo-dark.svg";

const API_BASE     = import.meta.env.VITE_API_BASE_URL as string;
const FRONTEND_VER = (import.meta.env.VITE_APP_VERSION as string) ?? "dev";

export const SIDEBAR_WIDTH = 216;
export const MOBILE_BP     = 768;

// ── Icons (stroked, 24-grid, sized down at the call site) ─────────────────────

type IconProps = { size?: number };
const stroke = {
  fill: "none", stroke: "currentColor", strokeWidth: 1.75,
  strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
};

const IconLive = ({ size = 17 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
    <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" />
  </svg>
);
const IconDetections = ({ size = 17 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconDevices = ({ size = 17 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
    <rect x="2" y="9" width="20" height="8" rx="2" />
    <line x1="6" y1="13" x2="6.01" y2="13" /><line x1="10" y1="13" x2="10.01" y2="13" />
    <path d="M6 9V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" />
  </svg>
);
const IconUpdates = ({ size = 17 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);
const IconUsers = ({ size = 17 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconKey = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} strokeWidth={2}>
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 1 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);
const IconSun = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} strokeWidth={2}>
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);
const IconMoon = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} strokeWidth={2}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);
const IconSignOut = ({ size = 14 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} strokeWidth={2}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const IconMenu = ({ size = 20 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} strokeWidth={2}>
    <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// ── Shared bits ───────────────────────────────────────────────────────────────

const EYEBROW: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: theme.textMuted,
};

const ICON_BUTTON: React.CSSProperties = {
  background: "transparent",
  border: `1px solid ${theme.border}`,
  color: theme.textSecondary,
  borderRadius: 5,
  padding: "5px 7px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
};

function useWindowWidth(): number {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

/** Today's detection count for the nav badge. */
function useTodayCount(deviceId: string): number | null {
  const { instance, accounts } = useMsal();
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { accessToken } = await instance.acquireTokenSilent({
          scopes: apiScopes,
          account: accounts[0],
        });
        const start = new Date(); start.setHours(0, 0, 0, 0);
        const params = new URLSearchParams({ start_date: start.toISOString() });
        if (deviceId) params.set("device_serial", deviceId);
        const r = await fetch(`${API_BASE}/api/detections/stats?${params}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!r.ok) throw new Error(String(r.status));
        const d = await r.json();
        // Only the real events. The badge is a call to attention, and the
        // other two buckets are precisely the ones nobody needs to look at —
        // counting them made a quiet day of pure duplicates read as a busy one.
        if (!cancelled) setCount(d.real);
      } catch {
        if (!cancelled) setCount(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [instance, accounts, deviceId]);

  return count;
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { to: "/",             end: true,  label: "Live Stream",   Icon: IconLive },
  { to: "/detections",   end: false, label: "Detections",    Icon: IconDetections, badge: true },
  { to: "/devices",      end: false, label: "Devices",       Icon: IconDevices },
  { to: "/orchestrator", end: false, label: "Model Updates", Icon: IconUpdates },
  // Managing external testers' email + password accounts is a staff job; a
  // tester signed in with one of those accounts does not get the entry (the
  // backend refuses their token on /api/auth/local/users anyway).
  { to: "/testers",      end: false, label: "Testers",       Icon: IconUsers, staffOnly: true },
  // Label Review is in the design but deliberately not listed yet: the only
  // review route today is /review/:taskId, reached from a batch on Model
  // Updates. A nav entry would point at a task list that does not exist, and a
  // dead link in the primary nav is worse than a missing one. Added with that
  // page.
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();
  const { deviceId, setDeviceId, routers, darkMode, toggleDarkMode, wsReadyState } = useAppContext();
  const location = useLocation();

  const isMobile = useWindowWidth() < MOBILE_BP;
  const [menuOpen, setMenuOpen] = useState(false);
  const asideRef = useRef<HTMLElement>(null);

  const logo     = darkMode ? intelliportLogoDark : intelliportLogo;
  // In a local (tester) session accounts[0] is the synthetic account built by
  // applyLocalAuth, so the tester's name shows here with no special casing.
  const userName = accounts[0]?.name ?? accounts[0]?.username ?? "";
  const todayCount = useTodayCount(deviceId);
  const localUser = isLocalAuth();
  const navItems = NAV_ITEMS.filter((item) => !item.staffOnly || !localUser);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const selectedRouter = routers.find((r) => r.serial_number === deviceId);
  const isOnline = selectedRouter?.online ?? false;
  const lastSeenLabel = (!isOnline && selectedRouter?.last_seen)
    ? new Date(selectedRouter.last_seen).toLocaleString("en-GB", {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : null;

  const [backendVer, setBackendVer] = useState<string>("…");
  useEffect(() => {
    fetch(`${API_BASE}/health`)
      .then((r) => r.json())
      .then((d) => setBackendVer(d.version ?? "?"))
      .catch(() => setBackendVer("?"));
  }, []);

  // Navigating closes the drawer; without this the rail stays over the page the
  // tap just opened.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => { if (!isMobile) setMenuOpen(false); }, [isMobile]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const wsColor = wsReadyState === "OPEN" ? theme.success
                : wsReadyState === "CONNECTING" ? theme.warning
                : theme.danger;

  const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 10,
    padding: "9px 12px", borderRadius: 5,
    background: isActive ? theme.goldSoft : "transparent",
    color: isActive ? theme.goldText : theme.textSecondary,
    fontSize: theme.fontSizeSm,
    fontWeight: isActive ? 700 : 400,
    textDecoration: "none",
    cursor: "pointer",
  });

  const rail = (
    <aside
      ref={asideRef}
      style={{
        position: isMobile ? "fixed" : "sticky",
        left: isMobile ? (menuOpen ? 0 : -SIDEBAR_WIDTH) : undefined,
        top: 0,
        height: "100vh",
        zIndex: 120,
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        background: theme.bgCard,
        borderRight: `1px solid ${theme.border}`,
        display: "flex",
        flexDirection: "column",
        transition: "left 0.2s ease",
      }}
    >
      {/* Brand */}
      <div style={{
        padding: "18px 18px 14px", borderBottom: `1px solid ${theme.border}`,
        display: "flex", flexDirection: "column", gap: 6,
      }}>
        <img src={logo} alt="Intelliport" style={{ height: 22, alignSelf: "flex-start" }} />
        <span style={EYEBROW}>Incident Detection</span>
      </div>

      {/* Device */}
      <div style={{
        padding: "12px 14px", borderBottom: `1px solid ${theme.border}`,
        display: "flex", flexDirection: "column", gap: 7,
      }}>
        <span style={EYEBROW}>Device</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            className={isOnline ? "dot-pulse" : undefined}
            style={{
              width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
              background: isOnline ? theme.success : theme.textMuted,
            }}
          />
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            style={{
              flex: 1, minWidth: 0,
              background: theme.bgInput, border: `1px solid ${theme.border}`,
              color: theme.textPrimary, borderRadius: 5, padding: "6px 8px",
              fontSize: theme.fontSizeXs, fontFamily: "inherit",
              cursor: "pointer", outline: "none",
            }}
          >
            {routers.length === 0
              ? <option value="">No devices</option>
              : routers.map((r) => (
                  <option key={r.serial_number} value={r.serial_number}>
                    {r.name ?? r.serial_number}
                  </option>
                ))}
          </select>
        </div>
        {lastSeenLabel && (
          <span style={{ fontSize: 10, color: theme.warning, letterSpacing: "0.02em" }}>
            Offline — last seen {lastSeenLabel}
          </span>
        )}
      </div>

      {/* Nav */}
      <nav style={{ display: "flex", flexDirection: "column", gap: 2, padding: "12px 10px", flex: 1 }}>
        {navItems.map(({ to, end, label, Icon, badge }) => (
          <NavLink key={to} to={to} end={end} style={navLinkStyle}>
            <Icon />
            <span style={{ flex: 1 }}>{label}</span>
            {badge && todayCount !== null && todayCount > 0 && (
              <span style={{
                fontSize: 11, fontWeight: 700,
                background: theme.goldSoft, color: theme.goldText,
                borderRadius: 9, padding: "1px 7px",
              }}>
                {todayCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div style={{
        padding: "12px 14px", borderTop: `1px solid ${theme.border}`,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{
          fontSize: theme.fontSizeXs, color: theme.textSecondary, flex: 1,
          minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {userName}
        </span>
        <button
          onClick={toggleDarkMode}
          title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          style={ICON_BUTTON}
        >
          {darkMode ? <IconSun /> : <IconMoon />}
        </button>
        {localUser && (
          <button onClick={() => setChangePasswordOpen(true)} title="Change password" style={ICON_BUTTON}>
            <IconKey />
          </button>
        )}
        {/* For a local session logoutRedirect is the patched one from
            applyLocalAuth: it clears the stored session and returns to "/". */}
        <button onClick={() => instance.logoutRedirect()} title="Sign out" style={ICON_BUTTON}>
          <IconSignOut />
        </button>
      </div>
      {changePasswordOpen && (
        <ChangePasswordDialog
          forced={false}
          onClose={() => setChangePasswordOpen(false)}
          onChanged={() => setChangePasswordOpen(false)}
        />
      )}

      {/* Live + versions — one line, as the design has it.

          It was split in two while the label spelled out the raw ready-state:
          "CONNECTING" plus a two-part version string does not fit 216px and the
          version wrapped mid-token. The fix is the label, not the layout — the
          dot already carries the state in its colour and the title spells it
          out on hover, so the text only has to be short enough to sit beside
          the versions. */}
      <div style={{
        padding: "10px 14px", borderTop: `1px solid ${theme.border}`,
        display: "flex", alignItems: "center", gap: 7,
      }}>
        <span
          className={wsReadyState === "OPEN" ? "dot-pulse" : undefined}
          title={`WebSocket: ${wsReadyState}`}
          style={{ width: 7, height: 7, borderRadius: "50%", background: wsColor, flexShrink: 0 }}
        />
        <span style={{
          fontSize: 11, color: theme.textMuted, letterSpacing: "0.04em", flexShrink: 0,
        }}>
          {wsReadyState === "OPEN" ? "LIVE" : wsReadyState === "CONNECTING" ? "···" : "OFF"}
        </span>
        <span
          title={`Frontend ${FRONTEND_VER} · Backend ${backendVer}`}
          style={{
            fontSize: 11, color: theme.textMuted, fontFamily: theme.fontMono,
            marginLeft: "auto", whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
          }}
        >
          FE {FRONTEND_VER} · BE {backendVer}
        </span>
      </div>
    </aside>
  );

  if (!isMobile) {
    return (
      <div style={{ display: "flex", alignItems: "flex-start", minHeight: "100vh" }}>
        {rail}
        <main style={{ flex: 1, minWidth: 0 }}>{children}</main>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Mobile top bar — just enough to reach the rail and see which device is live */}
      <div style={{
        position: "sticky", top: 0, zIndex: 110,
        height: 56, padding: "0 14px", flexShrink: 0,
        background: theme.bgCard, borderBottom: `1px solid ${theme.border}`,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={menuOpen}
          style={{ ...ICON_BUTTON, padding: "6px 8px" }}
        >
          <IconMenu />
        </button>
        <img src={logo} alt="Intelliport" style={{ height: 20 }} />
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
            background: isOnline ? theme.success : theme.textMuted,
          }} />
          <span style={{
            fontSize: theme.fontSizeXs, color: theme.textSecondary,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {selectedRouter?.name ?? deviceId ?? ""}
          </span>
        </span>
      </div>

      {rail}

      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 115 }}
        />
      )}

      <main style={{ flex: 1, minWidth: 0 }}>{children}</main>
    </div>
  );
}
